import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  type ComponentProps,
  type ReactNode,
} from 'react';
import './fonts-mono';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import styles from './AdminShell.module.css';
import { PrefetchLink } from './components/PrefetchLink';
import {
  ADMIN_LOTS,
  ADMIN_PRODUCTS,
  INVENTAIRE_CLUSTER_LABEL,
} from './constants/adminNavLabels';
import { adminAccess } from '@wapharma/domain';
import {
  PageShell,
  Nav,
  NavCluster,
  ProximityScope,
  NavClusterIconLink,
  NavAccountMenu,
  NavHeaderDivider,
  NavUtilityCluster,
  Loader,
  Group,
  Avatar,
  WP_ICON_NAV_MOBILE,
  Drawer,
  Stack,
  IconButton,
  useMediaQuery,
  useScrollbarReveal,
  ConfirmDeleteHost,
  Modal,
  ConfirmModalFooter,
  Text,
  tokens,
  type NavClusterItem,
} from '@wapharma/design-system';
import { navTwotoneIcon, navTwotoneItemIcon } from './lib/mxTwotoneIcon';
import {
  Bag2Twotone,
  BoxTwotone,
  ClipboardTextTwotone,
  DocumentTextTwotone,
  HamburgerMenuTwotone,
  HomeTwotone,
  LifebuoyTwotone,
  PeopleTwotone,
  Setting2Twotone,
  ShopTwotone,
  ShoppingCartTwotone,
  TagTwotone,
  TargetTwotone,
  TruckTickTwotone,
  UserEditTwotone,
} from './icons/mxNavTwotone';
import { SessionExpiryWarning } from './components/SessionExpiryWarning';
import { NavBarSearch } from './components/NavBarSearch';
import { EditProfileModal } from './components/EditProfileModal';
import { SettingsOverlay, type SettingsSectionKey } from './components/SettingsOverlay';
import { subscribeOpenSettingsOverlay } from './lib/settingsOverlay';
import mobileNavStyles from './components/adminMobileNav.module.css';
import a11yStyles from './adminShellA11y.module.css';
import { canAccessAdminApp } from '@wapharma/shared';
import { useAuth } from './contexts/AuthContext';
import { useToast } from './contexts/ToastContext';
import { ProductBulkImportProvider } from './contexts/ProductBulkImportContext';
import { InboxCountProvider } from './contexts/InboxCountContext';
import { InboxListProvider } from './contexts/InboxListContext';
import { NavCountsProvider } from './contexts/NavCountsContext';
import { useNavCounts, type NavCounts } from './contexts/NavCountsContext';
import type { AuthUser } from './contexts/AuthContext';
import { useInboxLivePoll } from './hooks/useInboxLivePoll';

import { InboxNavFlyout } from './components/InboxNavFlyout';
import { MessagesNavFlyout } from './components/MessagesNavFlyout';
import { ReportIssueWidget } from './components/ReportIssueWidget';
import { TourOverlay } from './components/TourOverlay';
import { OnboardingDock } from './components/OnboardingDock';
import { SetupWizard } from './components/SetupWizard';
import { GuideProvider } from './contexts/GuideContext';
import { JourneyProgressProvider } from './contexts/JourneyProgressContext';
import { TourProvider } from './contexts/TourContext';
import { logAdminNavTransition } from './lib/adminNavDebug';
import { prefetchRoutes } from './routing/prefetchRoute';

function ShellLink(props: ComponentProps<typeof Link>) {
  return <PrefetchLink {...props} />;
}

type ClusterDef = {
  key: string;
  label: string;
  triggerIcon: ReactNode;
  items: NavClusterItem[];
  /** Parcours force-click on the cluster trigger. */
  tourAnchor?: string;
};

function buildClusters(user: AuthUser | null, counts: NavCounts): ClusterDef[] {
  if (!user) return [];

  const access = adminAccess(user.packs, user.roles, user.grants);
  const c = (n: number) => n || undefined;
  const out: ClusterDef[] = [];

  if (access.seeCatalog || access.seeInventory) {
    const inventaireItems: NavClusterItem[] = [];
    if (access.seeCatalog) {
      inventaireItems.push({
        to: ADMIN_PRODUCTS.path,
        label: ADMIN_PRODUCTS.label,
        icon: navTwotoneItemIcon(TagTwotone),
        count: c(counts.products),
        tourAnchor: 'nav-catalog',
      });
    }
    if (access.seeInventory) {
      inventaireItems.push({
        to: ADMIN_LOTS.path,
        label: ADMIN_LOTS.shortLabel,
        icon: navTwotoneItemIcon(ClipboardTextTwotone),
      });
    }
    if (inventaireItems.length > 0) {
      out.push({
        key: 'inventaire',
        label: INVENTAIRE_CLUSTER_LABEL,
        triggerIcon: navTwotoneIcon(BoxTwotone),
        items: inventaireItems,
        tourAnchor: 'nav-inventaire',
      });
    }
  }

  const venteItems: NavClusterItem[] = [];
  if (access.seeOrders) {
    venteItems.push({
      to: '/orders',
      label: 'Commandes',
      icon: navTwotoneItemIcon(ShoppingCartTwotone),
      count: c(counts.orders),
    });
  }
  if (access.seeCustomers) {
    venteItems.push({
      to: '/customer',
      label: 'Clients',
      icon: navTwotoneItemIcon(PeopleTwotone),
      count: c(counts.customers),
    });
  }
  if (access.seeLeads) {
    venteItems.push({
      to: '/leads',
      label: 'Leads',
      icon: navTwotoneItemIcon(TargetTwotone),
      count: c(counts.leads),
    });
  }
  if (venteItems.length > 0) {
    out.push({
      key: 'vente',
      label: 'Vente',
      triggerIcon: navTwotoneIcon(Bag2Twotone),
      items: venteItems,
    });
  }

  if (access.seeDocuments) {
    out.push({
      key: 'pilotage',
      label: 'Pilotage',
      triggerIcon: navTwotoneIcon(DocumentTextTwotone),
      items: [
        {
          to: '/documents',
          label: 'Documents',
          icon: navTwotoneItemIcon(DocumentTextTwotone),
          count: c(counts.documents),
        },
      ],
    });
  }

  const achatsItems: NavClusterItem[] = [];
  if (access.seeProcurement) {
    achatsItems.push(
      {
        to: '/procurement/purchase-orders',
        label: 'Bons de commande',
        icon: navTwotoneItemIcon(ClipboardTextTwotone),
        count: c(counts.purchaseOrders),
      },
      {
        to: '/procurement/suppliers',
        label: 'Fournisseurs',
        icon: navTwotoneItemIcon(ShopTwotone),
        count: c(counts.suppliers),
      },
    );
  }
  if (access.seeConventions) {
    achatsItems.push({
      to: '/conventions',
      label: 'Conventions',
      icon: navTwotoneItemIcon(DocumentTextTwotone),
    });
  }
  if (achatsItems.length > 0) {
    out.push({
      key: 'achats',
      label: 'Achats',
      triggerIcon: navTwotoneIcon(TruckTickTwotone),
      items: achatsItems,
    });
  }

  const adminItems: NavClusterItem[] = [];
  if (access.seeSupport) {
    adminItems.push({
      to: '/support',
      label: 'Support',
      icon: navTwotoneItemIcon(LifebuoyTwotone),
      count: c(counts.supportTickets),
    });
  }
  // Nav hidden for now — route `/accounting` still exists for deep links.
  // if (access.seeAccounting) {
  //   adminItems.push({ to: '/accounting', label: 'Comptabilité', icon: navTwotoneItemIcon(BookTwotone) });
  // }
  if (access.seeParty) {
    adminItems.push({
      to: '/users',
      label: 'Utilisateurs',
      icon: navTwotoneItemIcon(UserEditTwotone),
      count: c(counts.users),
    });
  }
  // Nav hidden for now — storage route still exists for deep links.
  // if (access.seeStorage) {
  //   adminItems.push({
  //     to: ADMIN_STORAGE.path,
  //     label: ADMIN_STORAGE.label,
  //     icon: navTwotoneItemIcon(DocumentTextTwotone),
  //     tourAnchor: 'nav-storage',
  //   });
  // }
  if (adminItems.length > 0) {
    out.push({
      key: 'admin',
      label: 'Administration',
      triggerIcon: navTwotoneIcon(Setting2Twotone),
      items: adminItems,
    });
  }

  return out;
}


export function AdminShell() {
  const { user, loading, logout, mustChangePassword } = useAuth();
  const { showToast } = useToast();
  const loc = useLocation();
  const sessionBlockedRef = useRef(false);

  useEffect(() => {
    if (loading || !user || mustChangePassword || sessionBlockedRef.current) return;
    const mayEnter = canAccessAdminApp({
      email: user.email,
      roles: user.roles,
      packs: user.packs,
    });
    if (!user.active || !mayEnter) {
      sessionBlockedRef.current = true;
      showToast(
        !user.active ? 'Compte désactivé.' : "Ce compte n'a pas accès à l'administration.",
        { tone: 'error' },
      );
      void logout();
    }
  }, [loading, logout, mustChangePassword, showToast, user]);

  // Keep shell mounted once user known — flipping `loading` must not remount Outlet (Home refetch storm).
  // ShellProviders always wrap so auth gate swaps never orphan Catalog/Guide under a missing context
  // (and Vite HMR cannot leave consumers on a discarded createContext identity mid-session).
  let body: ReactNode;
  if (loading && !user) {
    body = (
      <div className={styles.loadingFallback}>
        <Loader />
      </div>
    );
  } else if (!user) {
    body = <Navigate to="/login" state={{ from: loc }} replace />;
  } else if (mustChangePassword) {
    body = <Navigate to="/force-password-change" replace />;
  } else {
    body = <AdminShellFrame user={user} logout={logout} />;
  }

  return <ShellProviders>{body}</ShellProviders>;
}

/** Auth gates above may swap the tree; keep provider identity stable for Outlet children. */
function ShellProviders({ children }: { children: ReactNode }) {
  return (
    <ProductBulkImportProvider>
      <InboxCountProvider>
        <InboxListProvider>
          <NavCountsProvider>
            <JourneyProgressProvider>
              <TourProvider>
                <GuideProvider>{children}</GuideProvider>
              </TourProvider>
            </JourneyProgressProvider>
          </NavCountsProvider>
        </InboxListProvider>
      </InboxCountProvider>
    </ProductBulkImportProvider>
  );
}

function AdminShellFrame({
  user,
  logout,
}: {
  user: AuthUser;
  logout: () => Promise<void>;
}) {
  useInboxLivePoll();
  useScrollbarReveal();
  const navCounts = useNavCounts();
  const loc = useLocation();
  const navigate = useNavigate();
  const narrowNav = useMediaQuery(tokens.media.mdMax);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<SettingsSectionKey>('general');
  const [settingsSopId, setSettingsSopId] = useState<string | null>(null);
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  useEffect(() => {
    return subscribeOpenSettingsOverlay((req) => {
      setSettingsSection(req.section ?? 'general');
      setSettingsSopId(req.sopId ?? null);
      setSettingsOpen(true);
    });
  }, []);

  const clusters = useMemo(() => buildClusters(user, navCounts), [user, navCounts]);
  const prevPathRef = useRef(loc.pathname);
  const accountLabel =
    [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean).join(' ') || user.email;

  useEffect(() => {
    const prev = prevPathRef.current;
    if (prev !== loc.pathname) {
      logAdminNavTransition(prev, loc.pathname);
      prevPathRef.current = loc.pathname;
    }
    setMobileNavOpen(false);
  }, [loc.pathname]);

  /** Shell owns the viewport: never let document scroll behind admin chrome. */
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyOverscroll = body.style.overscrollBehavior;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overscrollBehavior = prevHtmlOverscroll;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, []);

  /**
   * Outlet (`[data-wp-outlet-frame]`) is the real scrollport. Browser scroll restoration
   * remembers that element across reload and fights our mount reset → visible jump/fight.
   */
  useLayoutEffect(() => {
    const prev = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    return () => {
      history.scrollRestoration = prev;
    };
  }, []);

  /** Reset outlet scroll on route change + reload; keep header outside that scrollport. */
  useLayoutEffect(() => {
    const resetOutletScroll = () => {
      const scrollHost = document.querySelector<HTMLElement>('[data-wp-outlet-frame]');
      if (scrollHost) scrollHost.scrollTop = 0;
      window.scrollTo(0, 0);
    };

    resetOutletScroll();

    const main = document.getElementById('admin-main-content');
    if (main && typeof main.focus === 'function') {
      try {
        main.focus({ preventScroll: true });
      } catch {
        main.focus();
      }
    }

    /* Chrome may still apply element scroll restore after first paint — win the race. */
    const raf = window.requestAnimationFrame(resetOutletScroll);
    const t0 = window.setTimeout(resetOutletScroll, 0);
    const t1 = window.setTimeout(resetOutletScroll, 50);
    const onPageShow = () => resetOutletScroll();
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [loc.pathname]);

  const showSearch = adminAccess(user.packs, user.roles, user.grants).seeSearch;

  const navActive = (path: string) => {
    if (path === '/') return loc.pathname === '/';
    if (path === '/users') {
      return (
        loc.pathname === '/users' ||
        loc.pathname.startsWith('/users/') ||
        loc.pathname === '/roles' ||
        loc.pathname.startsWith('/roles/')
      );
    }
    return loc.pathname === path || loc.pathname.startsWith(`${path}/`);
  };

  const mobileNavPanel = narrowNav ? (
    <Drawer placement="left" opened={mobileNavOpen} onClose={() => setMobileNavOpen(false)} title="Navigation">
      <Stack gap="lg">
        {showSearch ? (
          <div className={mobileNavStyles.searchWrap}>
            <NavBarSearch />
          </div>
        ) : null}
        {clusters.map((c) => (
          <div key={c.key} className={mobileNavStyles.section}>
            <div className={mobileNavStyles.sectionLabel}>{c.label}</div>
            <ProximityScope axis="y" className={mobileNavStyles.linkGroup}>
              {c.items.map((it) => (
                <ShellLink
                  key={it.to}
                  to={it.to}
                  className={mobileNavStyles.link}
                  data-wp-proximity-item=""
                  data-tour={it.tourAnchor || undefined}
                  data-active={navActive(it.to) ? 'true' : 'false'}
                  aria-current={navActive(it.to) ? 'page' : undefined}
                >
                  {it.icon}
                  {it.label}
                </ShellLink>
              ))}
            </ProximityScope>
          </div>
        ))}
      </Stack>
    </Drawer>
  ) : null;

  return (
    <>
      <a href="#admin-main-content" className={a11yStyles.skipLink}>
        Aller au contenu principal
      </a>
      <PageShell
        contentId="admin-main-content"
        header={
        <Nav
          brand={
            <>
              {narrowNav ? (
                <>
                  <IconButton
                    type="button"
                    variant="soft"
                    size="md"
                    aria-label="Ouvrir le menu de navigation"
                    aria-expanded={mobileNavOpen}
                    data-tour="nav-menu"
                    onClick={() => setMobileNavOpen(true)}
                  >
                    {navTwotoneIcon(HamburgerMenuTwotone, { size: WP_ICON_NAV_MOBILE.size })}
                  </IconButton>
                  {mobileNavPanel}
                </>
              ) : null}
              <NavClusterIconLink
                to="/"
                label="Accueil"
                icon={navTwotoneIcon(HomeTwotone, narrowNav ? { size: WP_ICON_NAV_MOBILE.size } : undefined)}
                LinkComponent={ShellLink}
                active={loc.pathname === '/'}
              />
              {!narrowNav
                ? clusters.map((c) => (
                    <div
                      key={c.key}
                      onPointerEnter={() => prefetchRoutes(c.items.map((it) => it.to))}
                    >
                      <NavCluster
                        label={c.label}
                        triggerIcon={c.triggerIcon}
                        items={c.items}
                        tourAnchor={c.tourAnchor}
                        LinkComponent={ShellLink}
                        active={c.items.some((it) => navActive(it.to))}
                        isItemActive={(to) => navActive(to)}
                      />
                    </div>
                  ))
                : null}
            </>
          }
          end={
            <Group gap="sm" align="center" wrap="nowrap" className={styles.headerEnd}>
              {narrowNav ? null : showSearch ? <NavBarSearch /> : null}
              <NavUtilityCluster label="Boîte de réception et messages">
                <InboxNavFlyout />
                <MessagesNavFlyout />
              </NavUtilityCluster>
              <NavHeaderDivider />
              <NavAccountMenu
                displayName={accountLabel}
                email={user.email}
                identityAvatar={
                  <Avatar label={accountLabel} src={user.profilePictureUrl} size="md" />
                }
                triggerIcon={
                  <Avatar label={accountLabel} src={user.profilePictureUrl} size="nav" />
                }
                onLogout={() => setLogoutConfirmOpen(true)}
                extraItems={[
                  {
                    key: 'edit-profile',
                    label: 'Profil',
                    icon: navTwotoneItemIcon(UserEditTwotone),
                    onSelect: () => setEditProfileOpen(true),
                  },
                  {
                    key: 'settings',
                    label: 'Paramètres',
                    icon: navTwotoneItemIcon(Setting2Twotone),
                    onSelect: () => {
                      setSettingsSection('general');
                      setSettingsOpen(true);
                    },
                  },
                ]}
              />
            </Group>
          }
        />
      }
    >
        <Suspense
          fallback={
            <div className={styles.outletFallback}>
              <Loader />
            </div>
          }
        >
          <div className={`outletFrame ${styles.pageIn}`} data-path={loc.pathname}>
            <Outlet />
          </div>
        </Suspense>
      </PageShell>
      <SessionExpiryWarning />
      <OnboardingDock />
      <SetupWizard />
      <EditProfileModal opened={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
      <SettingsOverlay
        opened={settingsOpen}
        initialSection={settingsSection}
        initialSopId={settingsSopId}
        onClose={() => {
          setSettingsOpen(false);
          setSettingsSopId(null);
          if (loc.pathname === '/settings/data-sources') {
            navigate('/', { replace: true });
          }
        }}
        onReportIssue={() => setReportIssueOpen(true)}
      />
      <ReportIssueWidget
        hideTrigger
        defaultOpened={reportIssueOpen}
        onClosed={() => setReportIssueOpen(false)}
      />
      <TourOverlay />
      <Modal
        opened={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        title="Se déconnecter ?"
        footer={
          <ConfirmModalFooter
            cancelLabel="Annuler"
            confirmLabel="Se déconnecter"
            onCancel={() => setLogoutConfirmOpen(false)}
            onConfirm={() => {
              setLogoutConfirmOpen(false);
              void logout();
            }}
          />
        }
      >
        <Text size="sm">Vous allez quitter votre session admin.</Text>
      </Modal>
      <ConfirmDeleteHost />
    </>
  );
}
